import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import { EditorView } from '@codemirror/view';
import { StateEffect } from '@codemirror/state';
import { CkeditorConstants } from '../../src/utils/ckeditorconstants';

const SOURCE_DIALOG_CLASS = CkeditorConstants.class.CK_SOURCE_EDITING_ENHANCED_DIALOG;
const SOURCE_MODE_EVENT = 'wk:rhp-source-editing-mode';
const SOURCE_CONTEXT_EVENT = 'wk:rhp-source-editing-context';
const SOURCE_NAVIGATE_EVENT = 'wk:rhp-source-editing-navigate';
const SOURCE_DIRTY_EVENT = 'wk:rhp-source-editing-dirty';

const MODE_SWITCH_CLASS = 'wk-source-mode-switch';
const MODE_BUTTON_CLASS = 'wk-source-mode-button';
const MODE_BUTTON_ACTIVE_CLASS = 'wk-source-mode-button-active';
const MODE_BUTTON_FULL_CLASS = 'wk-source-mode-button-full';
const MODE_BUTTON_REFERENCE_CLASS = 'wk-source-mode-button-reference';
const MODE_NAV_CLASS = 'wk-source-mode-nav';
const MODE_NAV_BUTTON_CLASS = 'wk-source-mode-nav-button';
const MODE_NAV_INDEX_CLASS = 'wk-source-mode-nav-index';

const STYLES = `
.${MODE_SWITCH_CLASS}{display:none!important;}
.${MODE_BUTTON_CLASS}{display:inline-flex;align-items:center;justify-content:center;border:1px solid #d2d2d2;border-radius:3px;background:#eeeeee;color:#333;padding:4px 6px!important;font-size:13px;cursor:pointer;}
.${MODE_BUTTON_CLASS}.${MODE_BUTTON_ACTIVE_CLASS}{background:#4f9f39;border-color:#4f9f39;color:#fff;font-weight:600;}
.${MODE_NAV_CLASS}{display:none;align-items:center;gap:6px;}
.${MODE_NAV_BUTTON_CLASS}{display:inline-flex;align-items:center;justify-content:center;width:18px!important;height:24px!important;border:0;background:transparent;color:#666;border-radius:3px;cursor:pointer;}
.${MODE_NAV_BUTTON_CLASS}:hover{background:#ebebeb;color:#333;}
.${MODE_NAV_INDEX_CLASS}{color:#666;font-size:12px;min-width:24px;text-align:center!important;}
`;

export default class SourceEditingByReference extends Plugin {
  static get pluginName() {
    return 'SourceEditingByReference';
  }

  init() {
    if (
      this.editor.config.get('type') !== 'mini-editor' ||
      this.editor.config.get('sourceEditingByReference') !== true
    ) {
      return;
    }

    this._dialogOpenListenerBound = false;
    this._activeDialog = null;
    this._instanceId = `wk-byref-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    this._mode = 'full';
    this._context = null;
    this._fullSourceSnapshot = '';
    this._isProgrammaticDispatch = false;
    this._isCkeModelSync = false;
    this._userEditedInCurrentMode = false;
    this._cmUpdateListenerExtension = null;

    this._injectStyles();

    this._onSourceContext = (event) => {
      this._context = event?.detail || null;
      this._userEditedInCurrentMode = false;
      this._emitDirtyEvent(false);
      this._syncReferenceIndexForVisibleDialogs();
      this._syncVisibilityForVisibleDialogs();
      if (this._mode === 'byReference') {
        this._applyByReferenceContentToVisibleDialogs();
      }
    };

    this._onSourceMode = (event) => {
      this._mode = event?.detail?.mode === 'byReference' ? 'byReference' : 'full';
      this._syncModeStateForVisibleDialogs();
      this._syncVisibilityForVisibleDialogs();
      if (this._mode === 'byReference') {
        this._applyByReferenceContentToVisibleDialogs();
      } else {
        this._restoreFullForVisibleDialogs();
      }
    };

    window.addEventListener(SOURCE_CONTEXT_EVENT, this._onSourceContext);
    window.addEventListener(SOURCE_MODE_EVENT, this._onSourceMode);
    this._bindModelChangeListener();
    this._bindDialogLifecycle();
  }

  destroy() {
    if (this._onSourceContext) {
      window.removeEventListener(SOURCE_CONTEXT_EVENT, this._onSourceContext);
      this._onSourceContext = null;
    }

    if (this._onSourceMode) {
      window.removeEventListener(SOURCE_MODE_EVENT, this._onSourceMode);
      this._onSourceMode = null;
    }

    this._cleanupActiveDialog();
    return super.destroy();
  }

  _injectStyles() {
    const styleId = 'wk-source-by-reference-styles';
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.textContent = STYLES;
      return;
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  _bindModelChangeListener() {
    this.editor.model.document.on('change:data', () => {
      this._isCkeModelSync = true;
      setTimeout(() => { this._isCkeModelSync = false; }, 0);
    });
  }

  _bindDialogLifecycle() {
    if (this._dialogOpenListenerBound) {
      return;
    }

    const dialogPlugin = this.editor?.plugins?.get?.('Dialog');
    if (!dialogPlugin) {
      return;
    }

    dialogPlugin.on('change:isOpen', (_eventInfo, _propertyName, isOpen) => {
      if (isOpen) {
        setTimeout(() => this._handleDialogOpened(), 0);
      } else {
        this._handleDialogClosed();
      }
    });

    this._dialogOpenListenerBound = true;
  }

  _handleDialogOpened() {
    const activeDialog = this._resolveActiveDialog();
    if (!activeDialog) {
      return;
    }

    this._activeDialog = activeDialog;
    const owner = this._activeDialog.getAttribute('data-wk-byref-owner');
    if (!owner) {
      this._activeDialog.setAttribute('data-wk-byref-owner', this._instanceId);
      this._installInto(this._activeDialog);
    }

    this._syncModeStateForVisibleDialogs();
    this._syncVisibilityForVisibleDialogs();
    this._syncReferenceIndexForVisibleDialogs();
  }

  _handleDialogClosed() {
    this._cleanupActiveDialog();
    this._activeDialog = null;
    this._cmUpdateListenerExtension = null;
    this._userEditedInCurrentMode = false;
  }

  _resolveActiveDialog() {
    const sourceDialog = document.querySelector(`.ck-dialog-overlay .${SOURCE_DIALOG_CLASS}`);
    if (!sourceDialog) {
      return null;
    }

    return sourceDialog.closest('.ck-dialog') || sourceDialog;
  }

  _cleanupActiveDialog() {
    if (!this._activeDialog) {
      return;
    }

    this._activeDialog
      .querySelectorAll(`.${MODE_SWITCH_CLASS}[data-plugin-owner="${this._instanceId}"]`)
      .forEach((el) => el.remove());

    if (this._activeDialog.getAttribute('data-wk-byref-owner') === this._instanceId) {
      this._activeDialog.removeAttribute('data-wk-byref-owner');
    }
  }

  _installInto(ckDialog) {
    const existingModeSwitch = ckDialog.querySelector(
      `.${MODE_SWITCH_CLASS}[data-plugin-owner="${this._instanceId}"]`
    );
    if (existingModeSwitch) {
      return;
    }

    const modeSwitch = document.createElement('div');
    modeSwitch.className = MODE_SWITCH_CLASS;
    modeSwitch.dataset.pluginOwner = this._instanceId;

    const fullBtn = document.createElement('button');
    fullBtn.type = 'button';
    fullBtn.className = `${MODE_BUTTON_CLASS} ${MODE_BUTTON_FULL_CLASS}`;
    fullBtn.dataset.mode = 'full';
    fullBtn.textContent = 'Full';

    const byReferenceBtn = document.createElement('button');
    byReferenceBtn.type = 'button';
    byReferenceBtn.className = `${MODE_BUTTON_CLASS} ${MODE_BUTTON_REFERENCE_CLASS}`;
    byReferenceBtn.dataset.mode = 'byReference';
    byReferenceBtn.textContent = 'Reference';

    const nav = document.createElement('div');
    nav.className = MODE_NAV_CLASS;

    const previousBtn = document.createElement('button');
    previousBtn.type = 'button';
    previousBtn.className = MODE_NAV_BUTTON_CLASS;
    previousBtn.textContent = '<';
    previousBtn.title = 'Previous reference';

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = MODE_NAV_BUTTON_CLASS;
    nextBtn.textContent = '>';
    nextBtn.title = 'Next reference';

    const indexSpan = document.createElement('span');
    indexSpan.className = MODE_NAV_INDEX_CLASS;

    nav.appendChild(previousBtn);
    nav.appendChild(indexSpan);
    nav.appendChild(nextBtn);

    modeSwitch.appendChild(nav);
    modeSwitch.appendChild(byReferenceBtn);
    modeSwitch.appendChild(fullBtn);

    fullBtn.addEventListener('click', (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      if (this._mode === 'full') {
        return;
      }
      if (this._userEditedInCurrentMode && !window.confirm('You have unsaved changes. Discard and switch mode?')) {
        return;
      }
      this._userEditedInCurrentMode = false;
      this._emitDirtyEvent(false);
      this._mode = 'full';
      window.dispatchEvent(new CustomEvent(SOURCE_MODE_EVENT, { detail: { mode: 'full' } }));
      this._syncModeState(modeSwitch);
      this._syncVisibility(modeSwitch);
      this._restoreFullContent(ckDialog);
    });

    byReferenceBtn.addEventListener('click', (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      if (this._mode === 'byReference') {
        return;
      }
      if (this._userEditedInCurrentMode && !window.confirm('You have unsaved changes. Discard and switch mode?')) {
        return;
      }
      this._userEditedInCurrentMode = false;
      this._emitDirtyEvent(false);
      this._mode = 'byReference';
      window.dispatchEvent(new CustomEvent(SOURCE_MODE_EVENT, { detail: { mode: 'byReference' } }));
      this._syncModeState(modeSwitch);
      this._syncVisibility(modeSwitch);
      this._applyByReferenceContent(ckDialog);
    });

    previousBtn.addEventListener('click', (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      window.dispatchEvent(new CustomEvent(SOURCE_NAVIGATE_EVENT, { detail: { direction: 'previous' } }));
    });

    nextBtn.addEventListener('click', (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      window.dispatchEvent(new CustomEvent(SOURCE_NAVIGATE_EVENT, { detail: { direction: 'next' } }));
    });

    const header =
      ckDialog.querySelector('.ck-dialog__header') ||
      ckDialog.querySelector('.ck-form__header') ||
      ckDialog.querySelector('.ck-dialog-header');

    if (header) {
      const label = header.querySelector('.ck-form__header__label, .ck-dialog__header__label');
      if (label && label.nextSibling) {
        header.insertBefore(modeSwitch, label.nextSibling);
      } else {
        header.insertBefore(modeSwitch, header.firstChild);
      }
    } else {
      ckDialog.insertBefore(modeSwitch, ckDialog.firstChild);
    }

    const view = this._getCmView(ckDialog);
    this._fullSourceSnapshot = view?.state?.doc?.toString?.() || this._fullSourceSnapshot;
    this._installCmUpdateListener(view);

    this._mode = 'full';
    window.dispatchEvent(new CustomEvent(SOURCE_MODE_EVENT, { detail: { mode: 'full' } }));
    this._syncModeState(modeSwitch);
    this._syncVisibility(modeSwitch);
    this._syncReferenceIndex(modeSwitch);
    this._bindCloseInterceptor(ckDialog);
  }

  _bindCloseInterceptor(ckDialog) {
    if (ckDialog.__wkCloseInterceptorBound) {
      return;
    }

    const plugin = this;
    const restoreIfClean = () => {
      if (plugin._userEditedInCurrentMode || plugin._mode !== 'byReference') {
        return;
      }
      plugin._restoreFullContent(ckDialog);
    };

    // Restore full content before CKEditor's cancel handler compares against original
    ckDialog.addEventListener('click', (evt) => {
      const btn = evt.target.closest('button');
      if (!btn) { return; }
      if (btn.closest(`.${MODE_SWITCH_CLASS}`)) { return; }
      if (btn.classList.contains('ck-button-action')) { return; }
      restoreIfClean();
    }, true);

    ckDialog.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape') { restoreIfClean(); }
    }, true);

    ckDialog.__wkCloseInterceptorBound = true;
  }

  _getCmView(ckDialog) {
    const cmEl = ckDialog.querySelector('.cm-editor');
    if (!cmEl) {
      return null;
    }

    return EditorView.findFromDOM(cmEl);
  }

  _syncModeStateForVisibleDialogs() {
    if (!this._activeDialog) {
      return;
    }

    const modeSwitch = this._activeDialog.querySelector(
      `.${MODE_SWITCH_CLASS}[data-plugin-owner="${this._instanceId}"]`
    );
    if (!modeSwitch) {
      return;
    }

    this._syncModeState(modeSwitch);
  }

  _syncModeState(modeSwitch) {
    modeSwitch.querySelectorAll(`.${MODE_BUTTON_CLASS}`).forEach((button) => {
      button.classList.toggle(MODE_BUTTON_ACTIVE_CLASS, button.dataset.mode === this._mode);
    });
  }

  _syncVisibilityForVisibleDialogs() {
    if (!this._activeDialog) {
      return;
    }

    const modeSwitch = this._activeDialog.querySelector(
      `.${MODE_SWITCH_CLASS}[data-plugin-owner="${this._instanceId}"]`
    );
    if (!modeSwitch) {
      return;
    }

    this._syncVisibility(modeSwitch);
  }

  _syncVisibility(modeSwitch) {
    const nav = modeSwitch.querySelector(`.${MODE_NAV_CLASS}`);
    if (!nav) {
      return;
    }

    const isRhpContext = !!this._context?.fromRhp;
    nav.style.display = isRhpContext && this._mode === 'byReference' ? 'inline-flex' : 'none';
  }

  _syncReferenceIndexForVisibleDialogs() {
    if (!this._activeDialog) {
      return;
    }

    const modeSwitch = this._activeDialog.querySelector(
      `.${MODE_SWITCH_CLASS}[data-plugin-owner="${this._instanceId}"]`
    );
    if (!modeSwitch) {
      return;
    }

    this._syncReferenceIndex(modeSwitch);
  }

  _syncReferenceIndex(modeSwitch) {
    const indexEl = modeSwitch.querySelector(`.${MODE_NAV_INDEX_CLASS}`);
    if (!indexEl) {
      return;
    }

    const hasIndex = Number.isInteger(this._context?.referenceIndex) && this._context.referenceIndex >= 0;
    indexEl.textContent = hasIndex ? `${this._context.referenceIndex + 1}` : '';
  }

  _applyByReferenceContentToVisibleDialogs() {
    if (!this._activeDialog) {
      return;
    }

    if (this._activeDialog.getAttribute('data-wk-byref-owner') !== this._instanceId) {
      return;
    }

    this._applyByReferenceContent(this._activeDialog);
  }

  _applyByReferenceContent(ckDialog) {
    if (this._mode !== 'byReference') {
      return;
    }

    if (!this._context?.fromRhp || typeof this._context?.citationString !== 'string') {
      return;
    }

    const view = this._getCmView(ckDialog);
    if (!view) {
      return;
    }

    const current = view.state.doc.toString();
    if (!this._fullSourceSnapshot && current) {
      this._fullSourceSnapshot = current;
    }

    const citationValue = this._prettify(this._context.citationString);
    this._dispatchProgrammatic(view, {
      changes: { from: 0, to: view.state.doc.length, insert: citationValue },
      selection: { anchor: 0, head: 0 },
      scrollIntoView: true,
    });
  }

  _restoreFullForVisibleDialogs() {
    if (!this._activeDialog) {
      return;
    }

    if (this._activeDialog.getAttribute('data-wk-byref-owner') !== this._instanceId) {
      return;
    }

    this._restoreFullContent(this._activeDialog);
  }

  _restoreFullContent(ckDialog) {
    const view = this._getCmView(ckDialog);
    if (!view || typeof this._fullSourceSnapshot !== 'string') {
      return;
    }

    this._dispatchProgrammatic(view, {
      changes: { from: 0, to: view.state.doc.length, insert: this._fullSourceSnapshot },
      selection: { anchor: 0, head: 0 },
      scrollIntoView: true,
    });
  }

  _dispatchProgrammatic(view, transaction) {
    this._isProgrammaticDispatch = true;
    try {
      view.dispatch(transaction);
    } finally {
      this._isProgrammaticDispatch = false;
    }
  }

  _installCmUpdateListener(view) {
    if (!view || this._cmUpdateListenerExtension) {
      return;
    }

    const plugin = this;
    this._cmUpdateListenerExtension = EditorView.updateListener.of((update) => {
      if (!update.docChanged) {
        return;
      }
      if (plugin._isProgrammaticDispatch) {
        return;
      }
      if (plugin._isCkeModelSync) {
        plugin._fullSourceSnapshot = update.state.doc.toString();
        if (plugin._mode === 'byReference') {
          plugin._applyByReferenceContentToVisibleDialogs();
        }
        return;
      }
      if (!plugin._userEditedInCurrentMode) {
        plugin._userEditedInCurrentMode = true;
        plugin._emitDirtyEvent(true);
      }
    });

    view.dispatch({
      effects: StateEffect.appendConfig.of(this._cmUpdateListenerExtension),
    });
  }

  _emitDirtyEvent(isDirty) {
    window.dispatchEvent(new CustomEvent(SOURCE_DIRTY_EVENT, {
      detail: { isDirty, mode: this._mode },
    }));
  }

  _prettify(source) {
    if (typeof source !== 'string' || !source.trim()) {
      return '';
    }

    const compact = source.trim().replace(/>\s+</g, '>\n<');
    const lines = compact.split('\n');
    let indent = 0;

    const formatted = lines
      .map((rawLine) => {
        const line = rawLine.trim();
        if (!line) {
          return '';
        }

        const isClosingTag = /^<\//.test(line);
        const isOpeningTag = /^<[^/!][^>]*>$/.test(line);
        const isSelfClosingTag = /\/>$/.test(line);
        const isInlineWrap = /^<[^/!][^>]*>.*<\//.test(line);

        if (isClosingTag) {
          indent = Math.max(indent - 1, 0);
        }

        const output = `${'  '.repeat(indent)}${line}`;

        if (isOpeningTag && !isSelfClosingTag && !isInlineWrap) {
          indent += 1;
        }

        return output;
      })
      .filter(Boolean);

    return formatted.join('\n');
  }
}